import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import CopyLink from '../../components/CopyLink.jsx';
import './driver-directory-mini-crm.scss';

const BLUE = { color: '#005293' };
const MAKES = ['Tesla', 'Hyundai', 'BYD', 'Porsche', 'Nissan', 'Audi', 'Kia', 'BMW'];
const ENROLL_MAKES = ['BYD', 'Hyundai', 'Tesla', 'Porsche', 'Nissan', 'Kia', 'Audi'];
const CONNECTOR_CLASS = { CCS2: 'badge-ccs2', Type2: 'badge-type2', CHAdeMO: 'badge-chademo', GBT: 'badge-gbt' };
const PH_PHONE = /^\+63 ?9\d{2}[ -]?\d{3}[ -]?\d{4}$/;
const EMPTY_ENROLL = { fullName: '', email: '', phone: '', rfidTag: '', make: 'BYD', model: '', licensePlate: '', batteryCapacityKwh: '60.5', connectorType: 'CCS2' };

const peso = (v) => `₱${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString('en-US');
const dateOnly = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const initials = (name) => {
  if (!name) return 'EV';
  const parts = name.trim().split(/\s+/);
  return parts.length === 1 ? parts[0].slice(0, 2).toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const walletTier = (b) => (b < 100 ? ['badge-wallet-low', 'Low Float'] : b >= 1000 ? ['badge-wallet-high', 'High Tier'] : ['badge-wallet-funded', 'Funded']);
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function download(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }) {
  if (status === 'Active') return <span className="badge badge-success-soft"><i className="fas fa-check-circle me-1" />Active</span>;
  if (status === 'Suspended') return <span className="badge badge-danger-soft"><i className="fas fa-ban me-1" />Suspended</span>;
  return <span className="badge badge-warning-soft"><i className="fas fa-clock me-1" />Pending</span>;
}

export default function DriverDirectoryPage() {
  const [summary, setSummary] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('ALL');
  const [make, setMake] = useState('ALL');
  const [wallet, setWallet] = useState('ALL');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('Goodwill Credit');
  const [notes, setNotes] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [showEnroll, setShowEnroll] = useState(false);
  const [enroll, setEnroll] = useState(EMPTY_ENROLL);
  const [enrollError, setEnrollError] = useState('');
  const [enrolLink, setEnrolLink] = useState(null); // demo: { name, href } — no invitation email is sent
  const [busy, setBusy] = useState(false);

  const flash = (ok, msg) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadSummary = useCallback(() => {
    api.getDriverSummary().then(setSummary).catch((e) => setError(e.message));
  }, []);

  const loadDrivers = useCallback(() => {
    api.listDrivers({
      page, pageSize, q: query,
      status: status === 'ALL' ? '' : status, make: make === 'ALL' ? '' : make, wallet: wallet === 'ALL' ? '' : wallet,
    }).then((r) => { setDrivers(r.data); setTotal(r.meta.total); setError(''); }).catch((e) => setError(e.message));
  }, [page, pageSize, query, status, make, wallet]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const refresh = () => { loadSummary(); loadDrivers(); };

  const resetFilters = () => {
    setSearch(''); setQuery(''); setStatus('ALL'); setMake('ALL'); setWallet('ALL'); setPage(1);
  };

  const openDetail = async (id) => {
    try {
      setDetail(await api.getDriver(id));
    } catch (e) {
      setError(e.message);
    }
  };

  const openAdjust = (d) => {
    setAdjust(d);
    setAmount('');
    setNotes('');
    setReason('Goodwill Credit');
    setAdjustError('');
    setDetail(null);
  };

  const toggleRfid = async (d) => {
    if (!d.rfidTag) {
      flash(false, 'This driver does not have a linked physical RFID card.');
      return;
    }
    try {
      const u = await api.toggleDriverRfidLock(d.id);
      flash(true, `RFID Keyfob (${u.rfidTag}) is now ${u.rfidLocked ? 'LOCKED' : 'UNLOCKED / ACTIVE'} for driver ${u.fullName}.`);
      loadDrivers();
    } catch (e) {
      flash(false, e.message);
    }
  };

  const confirmAdjust = async () => {
    const value = parseFloat(amount);
    if (Number.isNaN(value) || value < 1 || value > 10000) {
      setAdjustError('Please enter a valid credit adjustment amount between ₱1.00 and ₱10,000.00.');
      return;
    }
    setBusy(true);
    setAdjustError('');
    try {
      const r = await api.adjustDriverWallet(adjust.id, { amount: value, reason, notes: notes.trim() || undefined });
      setAdjust(null);
      flash(true, `SUCCESS: ${peso(r.amount)} credit applied to ${r.driver.fullName}. Reason: ${r.reason}. Ledger ${r.transactionReference} posted; new balance ${peso(r.balanceAfter)}.`);
      refresh();
    } catch (e) {
      setAdjustError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEnroll = async () => {
    const f = enroll;
    if (!f.fullName.trim() || !f.email.trim() || !f.phone.trim()) {
      setEnrollError('Please fill in driver name, email, and phone number.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) {
      setEnrollError('Please enter a valid email address.');
      return;
    }
    if (!PH_PHONE.test(f.phone.trim())) {
      setEnrollError('Valid Philippine mobile number required (+63 9XX XXX XXXX).');
      return;
    }
    if (!f.model.trim() || !f.licensePlate.trim()) {
      setEnrollError('Vehicle model and license plate are required.');
      return;
    }
    setBusy(true);
    setEnrollError('');
    try {
      const d = await api.enrollDriver({
        fullName: f.fullName.trim(), email: f.email.trim(), phone: f.phone.trim(), rfidTag: f.rfidTag.trim() || undefined,
        vehicle: {
          make: f.make, model: f.model.trim(), licensePlate: f.licensePlate.trim(),
          batteryCapacityKwh: parseFloat(f.batteryCapacityKwh) || 60, connectorType: f.connectorType,
        },
      });
      setEnrolLink(d.demoSetPasswordLink ? { name: d.fullName, href: d.demoSetPasswordLink } : null);
      setShowEnroll(false);
      setEnroll(EMPTY_ENROLL);
      flash(true, `Registered driver profile created for ${d.fullName} with ₱500.00 welcome prepaid wallet credit!`);
      setPage(1);
      refresh();
    } catch (e) {
      setEnrollError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const exportDrivers = () => {
    const head = ['Name', 'Email', 'Phone', 'RFID', 'Plate', 'Make', 'Model', 'Battery kWh', 'Connector', 'Balance', 'Status', 'Member Since'];
    const rows = drivers.map((d) => [d.fullName, d.email, d.phone, d.rfidTag, d.vehicle?.licensePlate, d.vehicle?.make, d.vehicle?.model,
      d.vehicle?.batteryCapacityKwh, d.vehicle?.connectorType, d.wallet.balance.toFixed(2), d.status, dateOnly(d.createdAt)]);
    download('drivers.csv', [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\n'));
    flash(true, `Exported ${rows.length} driver(s) to drivers.csv.`);
  };

  const exportStatement = () => {
    const d = detail;
    const lines = [
      ['Driver Statement'], ['Name', d.fullName], ['Email', d.email], ['Phone', d.phone], ['Status', d.status], ['Member Since', dateOnly(d.createdAt)],
      ['Vehicle', d.vehicle ? `${d.vehicle.make} ${d.vehicle.model} ${d.vehicle.licensePlate || ''}` : 'None'],
      ['Wallet Balance', d.wallet.balance.toFixed(2)], [], ['Session Code', 'Commenced', 'kWh', 'Billed', 'Payment Status'],
      ...d.recentSessions.map((s) => [s.sessionCode, s.startedAt, s.totalEnergyKwh, s.totalAmount ?? '', s.paymentStatus]),
    ];
    download(`driver-statement-${d.id}.csv`, lines.map((r) => r.map(csvCell).join(',')).join('\n'));
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="container-fluid py-4" id="driver-crm-container">
      {toast && <div className={`toast-lite alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="driver-toast">{toast.msg}</div>}
      {enrolLink && (
        <div className="alert alert-info alert-dismissible small" role="note" id="driver-set-password-link">
          <i className="fas fa-flask me-1" aria-hidden="true" />Demo: no invitation email is sent. Give {enrolLink.name} this link to set a password and sign in: <CopyLink href={enrolLink.href} />
          <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => setEnrolLink(null)} />
        </div>
      )}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h4 className="fw-bold mb-0" style={BLUE}><i className="fas fa-id-card-alt me-2" style={BLUE} />Driver Directory &amp; Mini-CRM</h4>
            <span className="badge bg-primary ms-2">Customer 360</span>
          </div>
          <p className="text-muted small mb-0">Centralized EV driver profile management, connected vehicle telemetry, RFID card records, and prepaid wallet ledger support.</p>
        </div>
        <div className="mt-2 mt-md-0 d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary me-2" id="btn-export-all-drivers" onClick={exportDrivers}>
            <i className="fas fa-file-download me-1" /> Export this page (CSV)
          </button>
          <button className="btn btn-sm btn-primary" id="btn-enroll-driver" onClick={() => { setEnroll(EMPTY_ENROLL); setEnrollError(''); setShowEnroll(true); }}>
            <i className="fas fa-user-plus me-1" /> Enroll New Driver
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert" id="driver-error">{error}</div>}

      <div className="row mb-4">
        {[
          { label: 'Registered Drivers', id: 'stat-total-drivers', val: summary && num(summary.totalDrivers), color: '#005293', bg: '#e0f2fe', icon: 'fa-users', foot: <><span className="fw-bold" style={BLUE}>Network</span> roster of registered EV drivers</> },
          { label: 'Active Charging Today', id: 'stat-active-today', val: summary && num(summary.activeToday), color: '#16a34a', bg: '#ecfdf5', icon: 'fa-bolt', foot: <><span className="text-success fw-bold">{summary ? `${summary.activeTodayPct}%` : '—'}</span> daily active driver engagement</> },
          { label: 'Low Wallet Balance (<₱100)', id: 'stat-low-balance', val: summary && num(summary.lowBalance), color: '#dc2626', bg: '#fef2f2', icon: 'fa-wallet', foot: <><span className="text-warning fw-bold"><i className="fas fa-exclamation-triangle me-1" />Below ₱100</span> may not cover a session hold</> },
          { label: 'Linked RFID Cards', id: 'stat-rfid-count', val: summary && num(summary.rfidLinked), color: '#d97706', bg: '#fffbeb', icon: 'fa-wifi', foot: <><span className="fw-bold" style={BLUE}>{summary ? `${summary.rfidAdoptionPct}%` : '—'}</span> of drivers have a card on file</> },
        ].map((k) => (
          <div className="col-12 col-sm-6 col-xl-3 mb-3" key={k.id}>
            <div className="card border shadow-sm h-100 bg-white kpi-stat-card">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="text-muted small text-uppercase fw-bold">{k.label}</div>
                    <h3 className="fw-bold mb-0 mt-1" style={{ color: k.color }} id={k.id}>{k.val ?? '—'}</h3>
                  </div>
                  <div className="p-2 rounded" style={{ backgroundColor: k.bg, color: k.color }}><i className={`fas ${k.icon} fa-lg`} /></div>
                </div>
                <div className="mt-2 small text-muted">{k.foot}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card border shadow-sm bg-white mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-filter me-2" style={BLUE} />Driver Search &amp; Multi-Factor Filters</h6>
        </div>
        <div className="card-body p-3">
          <form id="driver-filter-form" onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); setPage(1); }}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted" htmlFor="search-keyword">Keyword Search</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
                  <input type="text" className="form-control" id="search-keyword" placeholder="Search name, email, phone, plate, RFID..."
                    value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-2">
                <label className="form-label small fw-bold text-muted" htmlFor="filter-account-status">Account Status</label>
                <select className="form-select form-select-sm" id="filter-account-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                  <option value="ALL">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Suspended">Suspended</option>
                  <option value="Pending">Pending Verification</option>
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-2">
                <label className="form-label small fw-bold text-muted" htmlFor="filter-vehicle-make">Vehicle Make</label>
                <select className="form-select form-select-sm" id="filter-vehicle-make" value={make} onChange={(e) => { setMake(e.target.value); setPage(1); }}>
                  <option value="ALL">All Makes</option>
                  {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-2">
                <label className="form-label small fw-bold text-muted" htmlFor="filter-wallet-balance">Wallet Balance</label>
                <select className="form-select form-select-sm" id="filter-wallet-balance" value={wallet} onChange={(e) => { setWallet(e.target.value); setPage(1); }}>
                  <option value="ALL">All Balances</option>
                  <option value="LOW">Low (&lt; ₱100)</option>
                  <option value="FUNDED">Funded (≥ ₱100)</option>
                  <option value="HIGH">High (≥ ₱1,000)</option>
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-2 d-flex align-items-end">
                <div className="w-100 d-flex gap-2">
                  <button type="submit" className="btn btn-sm btn-primary flex-grow-1 me-1" id="btn-apply-filters"><i className="fas fa-check me-1" /> Filter Drivers</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-reset-filters" title="Reset Filters" aria-label="Reset Filters" onClick={resetFilters}><i className="fas fa-undo" /></button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card border shadow-sm bg-white mb-4">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3">
          <div className="d-flex align-items-center">
            <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-list me-2" style={BLUE} />Registered Driver Roster</h6>
            <span className="badge bg-secondary ms-2 fw-bold" id="badge-result-count">Showing {total} Drivers</span>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
            <span className="text-muted small me-2">Rows per page:</span>
            <select className="form-select form-select-sm" id="select-page-size" style={{ width: 75 }} aria-label="Rows per page"
              value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              <option value="6">6</option>
              <option value="10">10</option>
              <option value="25">25</option>
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0" id="driver-directory-table">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th>Driver Details</th><th>Contact Info</th><th>Registered EV &amp; Inlet</th><th>Physical RFID Tag</th>
                  <th className="text-end">Prepaid Wallet</th><th>Status</th><th>Member Since</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody id="driver-table-body">
                {drivers.length === 0 && (
                  <tr><td colSpan={8} className="text-center p-4 text-muted"><i className="fas fa-search me-2" />No drivers match the selected filter criteria.</td></tr>
                )}
                {drivers.map((d) => {
                  const [tierClass, tierLabel] = walletTier(d.wallet.balance);
                  return (
                    <tr key={d.id} data-driver-id={d.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="driver-avatar-circle me-2">{initials(d.fullName)}</div>
                          <div>
                            <div className="fw-bold text-dark">{d.fullName}</div>
                            <div className="small text-muted font-monospace">ID #{d.id.slice(0, 8)}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="small text-dark font-monospace">{d.email}</div>
                        <div className="small text-muted">{d.phone || '—'}</div>
                      </td>
                      <td>
                        {d.vehicle ? (
                          <>
                            <div className="d-flex align-items-center mb-1">
                              <span className={`badge ${CONNECTOR_CLASS[d.vehicle.connectorType] || 'bg-secondary'} me-1 fw-bold`}>{d.vehicle.connectorType}</span>
                              <strong className="text-dark small">{d.vehicle.make} {d.vehicle.model}</strong>
                            </div>
                            <div className="small text-muted font-monospace">{d.vehicle.licensePlate || '—'} &bull; {d.vehicle.batteryCapacityKwh} kWh</div>
                          </>
                        ) : <span className="text-muted small">No vehicle registered</span>}
                      </td>
                      <td>
                        {d.rfidTag ? (
                          <>
                            <div className="font-monospace small fw-bold text-dark">{d.rfidTag}</div>
                            {d.rfidLocked
                              ? <span className="badge badge-rfid-locked ms-1"><i className="fas fa-lock me-1" />Locked</span>
                              : <span className="badge badge-rfid-active ms-1"><i className="fas fa-check-circle me-1" />Active</span>}
                          </>
                        ) : <span className="text-muted small">No RFID Tag</span>}
                      </td>
                      <td className="text-end">
                        <div className="fw-bold font-monospace text-dark">{peso(d.wallet.balance)}</div>
                        <span className={`badge ${tierClass}`}>{tierLabel}</span>
                      </td>
                      <td><StatusBadge status={d.status} /></td>
                      <td className="small text-muted">{dateOnly(d.createdAt)}</td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary btn-view-crm" title="View CRM 360" aria-label="View CRM Profile" onClick={() => openDetail(d.id)}><i className="fas fa-address-card" /></button>
                          <button className="btn btn-outline-warning btn-toggle-rfid" title="Toggle RFID Lock" aria-label="Toggle RFID Lock" onClick={() => toggleRfid(d)}>
                            <i className={`fas ${d.rfidLocked ? 'fa-lock-open' : 'fa-lock'}`} />
                          </button>
                          <button className="btn btn-outline-success btn-credit-wallet" title="Credit Wallet" aria-label="Credit Wallet" onClick={() => openAdjust(d)}><i className="fas fa-coins" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
          <span className="text-muted small">
            Showing <strong id="driver-range-start">{rangeStart}</strong>–<strong id="driver-range-end">{rangeEnd}</strong> of <strong id="driver-total-count">{total}</strong> drivers
            {summary ? ` (Network roster: ${num(summary.totalDrivers)} drivers)` : ''}
          </span>
          <nav aria-label="Driver pagination">
            <ul className="pagination pagination-sm mb-0" id="driver-pagination">
              {total > pageSize && (
                <>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button type="button" className="page-link" aria-label="Previous page" onClick={() => setPage(page - 1)}>&laquo;</button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                      <button type="button" className="page-link" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                    <button type="button" className="page-link" aria-label="Next page" onClick={() => setPage(page + 1)}>&raquo;</button>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {detail && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="crm-modal-name" id="modal-driver-detail">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white border-0">
              <div className="modal-header border-bottom py-3">
                <div className="d-flex align-items-center">
                  <div className="driver-avatar-circle me-3" id="crm-modal-avatar">{initials(detail.fullName)}</div>
                  <div>
                    <h5 className="modal-title fw-bold mb-0" style={BLUE} id="crm-modal-name">{detail.fullName}</h5>
                    <span className="text-muted small font-monospace" id="crm-modal-email">{detail.email}</span>
                  </div>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDetail(null)} />
              </div>
              <div className="modal-body p-4">
                <div className="d-flex flex-wrap gap-2 mb-4 align-items-center">
                  <span className={`badge px-2 py-1 ${detail.status === 'Active' ? 'bg-success' : detail.status === 'Suspended' ? 'bg-danger' : 'bg-warning text-dark'}`} id="crm-modal-status-badge">{detail.status} Account</span>
                  <span className="badge bg-primary px-2 py-1" id="crm-modal-role-badge">Driver Role</span>
                  <span className="text-muted small ms-auto" id="crm-modal-joined">Joined {dateOnly(detail.createdAt)}</span>
                </div>
                <div className="row mb-4">
                  <div className="col-12 col-md-6 mb-3 mb-md-0">
                    <div className="card bg-light border h-100"><div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-uppercase small fw-bold" style={BLUE}><i className="fas fa-car me-1" /> Registered Vehicle</span>
                        {detail.vehicle && <span className={`badge ${CONNECTOR_CLASS[detail.vehicle.connectorType] || 'bg-secondary'}`} id="crm-modal-connector">{detail.vehicle.connectorType}</span>}
                      </div>
                      {detail.vehicle ? (
                        <>
                          <h6 className="fw-bold text-dark mb-1" id="crm-modal-vehicle">{detail.vehicle.make} {detail.vehicle.model}{detail.vehicle.year ? ` (${detail.vehicle.year})` : ''}</h6>
                          <div className="small text-muted mb-2">License Plate: <strong className="text-dark font-monospace" id="crm-modal-plate">{detail.vehicle.licensePlate || '—'}</strong></div>
                          <div className="d-flex justify-content-between small text-muted pt-2 border-top">
                            <span>Battery Capacity:</span><strong className="text-dark" id="crm-modal-battery">{detail.vehicle.batteryCapacityKwh} kWh</strong>
                          </div>
                        </>
                      ) : <p className="small text-muted mb-0">No vehicle registered.</p>}
                    </div></div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="card bg-light border h-100"><div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-uppercase small fw-bold" style={BLUE}><i className="fas fa-wallet me-1" /> Prepaid Wallet</span>
                        <span className="badge badge-success-soft fw-bold">Prepaid wallet</span>
                      </div>
                      <div className="d-flex align-items-baseline mb-1">
                        <h4 className="fw-bold text-success mb-0" id="crm-modal-balance">{peso(detail.wallet.balance)}</h4>
                        <span className="text-muted small ms-2">({peso(detail.wallet.heldBalance)} held)</span>
                      </div>
                      <div className="small text-muted mb-2">Auto-Reload: <strong className="text-dark" id="crm-modal-autoreload">
                        {detail.wallet.autoReloadEnabled ? `Enabled (${peso(detail.wallet.autoReloadThreshold)} threshold)` : 'Disabled'}</strong></div>
                      <div className="d-flex justify-content-between align-items-center small text-muted pt-2 border-top">
                        <span>RFID Keyfob UID:</span><code className="text-primary fw-bold" id="crm-modal-rfid">{detail.rfidTag || '-'}</code>
                      </div>
                    </div></div>
                  </div>
                </div>
                <h6 className="fw-bold mb-3" style={BLUE}><i className="fas fa-history me-2" style={BLUE} />Recent Charging Sessions (Live Ledger Linked)</h6>
                <div className="table-responsive border rounded bg-white mb-3">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="bg-light">
                      <tr className="text-muted small text-uppercase">
                        <th>Session Code</th><th>Commenced</th><th className="text-end">Delivered (kWh)</th><th className="text-end">Billed Amount</th><th>Payment Status</th>
                      </tr>
                    </thead>
                    <tbody id="crm-modal-sessions-body">
                      {detail.recentSessions.length === 0 && <tr><td colSpan={5} className="text-center p-3 text-muted">No charging sessions logged yet.</td></tr>}
                      {detail.recentSessions.map((s) => (
                        <tr key={s.id}>
                          <td className="font-monospace fw-bold" style={BLUE}>{s.sessionCode}</td>
                          <td className="small text-muted">{new Date(s.startedAt).toLocaleString('en-PH')}</td>
                          <td className="text-end font-monospace text-dark">{s.totalEnergyKwh} kWh</td>
                          <td className="text-end font-monospace fw-bold text-success">{s.totalAmount == null ? '—' : peso(s.totalAmount)}</td>
                          <td><span className="badge badge-success-soft">{s.paymentStatus}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light py-2 px-4 d-flex justify-content-between">
                <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-crm-export-statement" onClick={exportStatement}>
                  <i className="fas fa-file-csv me-1" /> Export Driver Statement (CSV)
                </button>
                <div>
                  <button type="button" className="btn btn-sm btn-outline-primary me-2" id="btn-crm-adjust-balance" onClick={() => openAdjust(detail)}>
                    <i className="fas fa-coins me-1" /> Adjust Wallet
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" id="btn-crm-close" onClick={() => setDetail(null)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {adjust && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="adjust-modal-title" id="modal-wallet-adjustment">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white border-0">
              <div className="modal-header border-bottom py-3">
                <h5 className="modal-title fw-bold" style={BLUE} id="adjust-modal-title"><i className="fas fa-coins me-2" style={{ color: '#fecc00' }} />Manual Wallet Credit Adjustment</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setAdjust(null)} />
              </div>
              <div className="modal-body p-4">
                <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div className="small text-muted fw-bold text-uppercase">Target Driver Account</div>
                  <div className="fw-bold text-dark" id="adjust-modal-driver-name">{adjust.fullName}</div>
                  <div className="small text-muted font-monospace" id="adjust-modal-driver-email">{adjust.email}</div>
                  <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                    <span className="small text-muted">Current Balance:</span>
                    <strong className="text-success font-monospace" id="adjust-modal-current-balance">{peso(adjust.wallet.balance)}</strong>
                  </div>
                </div>
                <form id="form-wallet-adjust" onSubmit={(e) => e.preventDefault()}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold" htmlFor="adjust-amount-input">Credit Amount (PHP) <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text fw-bold">₱</span>
                      <input type="number" className="form-control" id="adjust-amount-input" min="1" max="10000" step="1" required placeholder="e.g. 250"
                        value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </div>
                    <small className="form-text text-muted">Allowable limits: ₱1.00 to ₱10,000.00 per CSR intervention.</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold" htmlFor="adjust-reason-select">Operational Adjustment Reason <span className="text-danger">*</span></label>
                    <select className="form-select" id="adjust-reason-select" required value={reason} onChange={(e) => setReason(e.target.value)}>
                      <option value="Goodwill Credit">Goodwill Support Credit (Customer Satisfaction)</option>
                      <option value="Charging Glitch Reimbursement">Charging Glitch Reimbursement (EVSE Offline)</option>
                      <option value="Cash Deposit Override">Cash Over-the-Counter Deposit Override</option>
                      <option value="Promotion Incentive">Marketing / Fleet Promotion Incentive</option>
                    </select>
                  </div>
                  <div className="mb-0">
                    <label className="form-label small fw-bold" htmlFor="adjust-notes-input">Auditor Narrative Notes</label>
                    <textarea className="form-control" id="adjust-notes-input" rows={2} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Provide context for the double-entry general ledger entry..." />
                  </div>
                  {adjustError && <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert" id="adjust-error">{adjustError}</div>}
                </form>
              </div>
              <div className="modal-footer bg-light py-2 px-4">
                <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-cancel-wallet-adjust" onClick={() => setAdjust(null)}>Cancel</button>
                <button type="button" className="btn btn-sm btn-success fw-bold" id="btn-confirm-wallet-adjust" onClick={confirmAdjust} disabled={busy}>
                  {busy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-check me-1" />} Confirm Adjustment &amp; Post Ledger
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEnroll && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="enroll-modal-title" id="modal-quick-driver">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white border-0">
              <div className="modal-header border-bottom py-3">
                <h5 className="modal-title fw-bold" style={BLUE} id="enroll-modal-title"><i className="fas fa-user-plus me-2" style={BLUE} />Enroll Registered EV Driver</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowEnroll(false)} />
              </div>
              <div className="modal-body p-4">
                <form id="form-enroll-driver" onSubmit={(e) => e.preventDefault()}>
                  <div className="row mb-3">
                    <div className="col-12 col-md-6 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-name">Full Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" id="enroll-name" required placeholder="e.g. Eduardo Dela Cruz" value={enroll.fullName} onChange={(e) => setEnroll({ ...enroll, fullName: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-email">Email Address (Login ID) <span className="text-danger">*</span></label>
                      <input type="email" className="form-control" id="enroll-email" required placeholder="e.g. eduardo.delacruz@gmail.com" value={enroll.email} onChange={(e) => setEnroll({ ...enroll, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12 col-md-6 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-phone">Mobile Phone <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" id="enroll-phone" required placeholder="+63 917 555 0199" value={enroll.phone} onChange={(e) => setEnroll({ ...enroll, phone: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-rfid">Physical RFID Card UID (Optional)</label>
                      <input type="text" className="form-control font-monospace" id="enroll-rfid" placeholder="e.g. E200410668120255" value={enroll.rfidTag} onChange={(e) => setEnroll({ ...enroll, rfidTag: e.target.value })} />
                    </div>
                  </div>
                  <hr className="my-3" />
                  <h6 className="fw-bold mb-3" style={BLUE}><i className="fas fa-car me-2" />Primary Vehicle Profile</h6>
                  <div className="row mb-3">
                    <div className="col-12 col-sm-6 col-md-3 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-make">Vehicle Make <span className="text-danger">*</span></label>
                      <select className="form-select" id="enroll-make" required value={enroll.make} onChange={(e) => setEnroll({ ...enroll, make: e.target.value })}>
                        {ENROLL_MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-sm-6 col-md-3 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-model">Model Designation <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" id="enroll-model" required placeholder="e.g. Atto 3" value={enroll.model} onChange={(e) => setEnroll({ ...enroll, model: e.target.value })} />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-plate">License Plate <span className="text-danger">*</span></label>
                      <input type="text" className="form-control font-monospace" id="enroll-plate" required placeholder="ABC 1234" value={enroll.licensePlate} onChange={(e) => setEnroll({ ...enroll, licensePlate: e.target.value })} />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3 mb-2">
                      <label className="form-label small fw-bold" htmlFor="enroll-battery">Battery Pack (kWh)</label>
                      <input type="number" className="form-control" id="enroll-battery" step="0.1" value={enroll.batteryCapacityKwh} onChange={(e) => setEnroll({ ...enroll, batteryCapacityKwh: e.target.value })} />
                    </div>
                  </div>
                  <div className="mb-0">
                    <label className="form-label small fw-bold" htmlFor="enroll-connector">Connector Inlet Standard <span className="text-danger">*</span></label>
                    <select className="form-select" id="enroll-connector" required value={enroll.connectorType} onChange={(e) => setEnroll({ ...enroll, connectorType: e.target.value })}>
                      <option value="CCS2">CCS2 (Combined Charging System 2) — European/PH DC Fast</option>
                      <option value="Type2">Type 2 (Mennekes) — AC Standard</option>
                      <option value="CHAdeMO">CHAdeMO — Japanese DC Standard</option>
                      <option value="GBT">GB/T — Chinese Standard</option>
                    </select>
                  </div>
                  {enrollError && <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert" id="enroll-error">{enrollError}</div>}
                </form>
              </div>
              <div className="modal-footer bg-light py-2 px-4">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowEnroll(false)}>Cancel</button>
                <button type="button" className="btn btn-sm btn-primary fw-bold" id="btn-save-new-driver" onClick={saveEnroll} disabled={busy}>
                  <i className="fas fa-check me-1" /> Register &amp; Provision Wallet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
